/**
 * One decision for both "Get the app" surfaces on the web — the slim bar
 * (WorkerAppDownloadBanner) and the Home action item (dashboard.tsx). Reads the
 * Firestore switch once per page load, detects the phone platform, honours the
 * shared 30-day "Not now", and hides when Chrome on Android reports the app
 * installed. Web-only by nature; no c1_app counterpart.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';

import { db } from '../firebase';
import {
  ANDROID_PACKAGE_ID,
  APP_BANNER_DISMISSED_EVENT,
  APP_BANNER_DISMISS_MS,
  APP_BANNER_PREVIEW_PARAM,
  APP_BANNER_PREVIEW_SESSION_KEY,
  WORKER_APP_BANNER_OFF,
  WORKER_APP_BANNER_SETTINGS_DOC,
  appBannerDismissKey,
  detectAppBannerPlatform,
  parseWorkerAppBannerConfig,
  resolveWorkerAppBanner,
  type AppBannerDecision,
  type AppBannerPlatform,
  type WorkerAppBannerConfig,
} from '../utils/workerAppBanner';

let configPromise: Promise<WorkerAppBannerConfig> | null = null;
/** One read per page load; a flipped switch reaches workers on their next visit. */
export function loadWorkerAppBannerConfig(): Promise<WorkerAppBannerConfig> {
  if (!configPromise) {
    configPromise = getDoc(doc(db, WORKER_APP_BANNER_SETTINGS_DOC))
      .then((snap) => parseWorkerAppBannerConfig(snap.exists() ? snap.data() : null))
      .catch(() => WORKER_APP_BANNER_OFF);
  }
  return configPromise;
}

export function readAppBannerPreviewFlag(search: string): boolean {
  try {
    const value = new URLSearchParams(search).get(APP_BANNER_PREVIEW_PARAM);
    if (value === 'preview') window.sessionStorage.setItem(APP_BANNER_PREVIEW_SESSION_KEY, '1');
    if (value === 'off') window.sessionStorage.removeItem(APP_BANNER_PREVIEW_SESSION_KEY);
    return window.sessionStorage.getItem(APP_BANNER_PREVIEW_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

export function readAppBannerDismissedUntil(uid: string): number {
  try {
    const raw = window.localStorage.getItem(appBannerDismissKey(uid));
    const until = raw ? Number(raw) : 0;
    return Number.isFinite(until) ? until : 0;
  } catch {
    return 0;
  }
}

/** "Not now" on either surface: 30 days on this browser, and tell the other surface now. */
export function dismissAppBanner(uid: string): number {
  const until = Date.now() + APP_BANNER_DISMISS_MS;
  try {
    window.localStorage.setItem(appBannerDismissKey(uid), String(until));
  } catch {
    // Storage blocked: hide for this page view only.
  }
  try {
    window.dispatchEvent(new CustomEvent(APP_BANNER_DISMISSED_EVENT, { detail: { uid, until } }));
  } catch {
    /* ignore */
  }
  return until;
}

export function detectCurrentAppBannerPlatform(): AppBannerPlatform | null {
  return typeof navigator === 'undefined'
    ? null
    : detectAppBannerPlatform(navigator.userAgent || '', navigator.maxTouchPoints || 0);
}

type RelatedApp = { platform?: string; id?: string };
type NavigatorWithRelatedApps = Navigator & { getInstalledRelatedApps?: () => Promise<RelatedApp[]> };

export interface WorkerAppBannerDecisionState {
  decision: AppBannerDecision | null;
  platform: AppBannerPlatform | null;
  config: WorkerAppBannerConfig;
  installed: boolean;
  dismissedUntil: number;
  preview: boolean;
  dismiss: () => void;
}

export function useWorkerAppBannerDecision(uid: string | null | undefined, pathname: string, search: string): WorkerAppBannerDecisionState {
  const [config, setConfig] = useState<WorkerAppBannerConfig>(WORKER_APP_BANNER_OFF);
  const [installed, setInstalled] = useState(false);
  const [dismissedUntil, setDismissedUntil] = useState(0);
  const preview = useMemo(() => readAppBannerPreviewFlag(search), [search]);
  const platform = useMemo(() => detectCurrentAppBannerPlatform(), []);

  useEffect(() => {
    let cancelled = false;
    if (!uid || (!platform && !preview)) return undefined;
    void loadWorkerAppBannerConfig().then((next) => {
      if (!cancelled) setConfig(next);
    });
    return () => {
      cancelled = true;
    };
  }, [uid, platform, preview]);

  useEffect(() => {
    if (!uid) return undefined;
    setDismissedUntil(readAppBannerDismissedUntil(uid));
    const onDismissed = (e: Event) => {
      const detail = (e as CustomEvent<{ uid?: string; until?: number }>).detail;
      if (!detail || detail.uid === uid) setDismissedUntil(typeof detail?.until === 'number' ? detail.until : Date.now() + APP_BANNER_DISMISS_MS);
    };
    window.addEventListener(APP_BANNER_DISMISSED_EVENT, onDismissed);
    return () => window.removeEventListener(APP_BANNER_DISMISSED_EVENT, onDismissed);
  }, [uid]);

  // Best effort: Chrome on Android can report the installed app when it is
  // listed in manifest.json related_applications. Anything else → not installed.
  useEffect(() => {
    let cancelled = false;
    const nav = typeof navigator === 'undefined' ? undefined : (navigator as NavigatorWithRelatedApps);
    if (platform !== 'android' || !nav?.getInstalledRelatedApps) return undefined;
    nav
      .getInstalledRelatedApps()
      .then((apps) => {
        if (!cancelled) setInstalled(apps.some((app) => app.id === ANDROID_PACKAGE_ID));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [platform]);

  const dismiss = useCallback(() => {
    if (uid) setDismissedUntil(dismissAppBanner(uid));
  }, [uid]);

  const decision = useMemo(
    () => (uid ? resolveWorkerAppBanner({ platform, config, pathname, dismissedUntil, now: Date.now(), installed, preview }) : null),
    [uid, platform, config, pathname, dismissedUntil, installed, preview],
  );

  return { decision, platform, config, installed, dismissedUntil, preview, dismiss };
}
